import React, { useEffect, useState } from 'react';
import {
  TextField,
  Box,
  Typography,
  Alert,
  Button,
  IconButton,
  Card,
  CardMedia,
  CardActions,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import api from '../../../config/api';

interface Attachment {
  id: number;
  fileName: string;
  fileUrl: string;
  fileSize: number;
}

interface Props {
  data: any;
  onChange: (data: any) => void;
  onComplete: (completed: boolean) => void;
}

const DetailedDescription: React.FC<Props> = ({ data, onChange, onComplete }) => {
  const { id: formId } = useParams<{ id: string }>();
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    fetchAttachments();
  }, []);

  const fetchAttachments = async () => {
    try {
      const response = await api.get(`/forms/${formId}/attachments/detailed_description`);
      setAttachments(response.data.attachments || []);
    } catch (error) {
      console.error('Failed to fetch attachments:', error);
    }
  };

  const handleChange = (field: string, value: any) => {
    const newData = { ...data, [field]: value };
    onChange(newData);
    const isComplete = !!(newData.technicalDescriptionAndWorkingPrinciple?.length >= 300);
    onComplete(isComplete);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Only JPG, JPEG, and PNG images are allowed');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('File size must be less than 2MB');
      return;
    }

    // Check if adding this file exceeds 10 attachments
    if (attachments.length >= 10) {
      setUploadError('Maximum 10 attachments allowed');
      return;
    }

    // Check total size (10MB limit)
    const totalSize = attachments.reduce((sum, att) => sum + att.fileSize, 0);
    if (totalSize + file.size > 10 * 1024 * 1024) {
      setUploadError('Total attachments size cannot exceed 10MB');
      return;
    }

    setUploadError('');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fieldName', 'detailed_description');

      const response = await api.post(`/forms/${formId}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setAttachments([...attachments, response.data.attachment]);
    } catch (error: any) {
      setUploadError(error.response?.data?.error || 'Failed to upload file');
    } finally {
      setUploading(false);
      // Reset input
      event.target.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;

    try {
      await api.delete(`/forms/${formId}/attachments/${attachmentId}`);
      setAttachments(attachments.filter((att) => att.id !== attachmentId));
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete attachment');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getTotalSize = () => {
    const total = attachments.reduce((sum, att) => sum + att.fileSize, 0);
    return formatFileSize(total);
  };

  useEffect(() => {
    const isComplete = !!(data.technicalDescriptionAndWorkingPrinciple?.length >= 300);
    onComplete(isComplete);
  }, [data]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Detailed Description</Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Include the following:
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1.5, mb: 0.5 }}>
          Structure / Architecture
        </Typography>
        <Typography variant="body2" component="div" sx={{ ml: 1, mb: 1 }}>
          • For mechanical inventions: parts, materials, assembly, and interaction.<br />
          • For electronics: circuit diagrams, signal flow, key components, control logic.<br />
          • For software: algorithms, flowcharts, data structures, modules, input/output behavior.<br />
          • For chemical processes: compositions, concentrations, temperatures, pressures, process steps.
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1.5, mb: 0.5 }}>
          Operation / Working Principle
        </Typography>
        <Typography variant="body2" component="div" sx={{ ml: 1, mb: 1 }}>
          • How the invention functions step-by-step.<br />
          • Any control sequences, decision logic, or feedback mechanisms.
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1.5, mb: 0.5 }}>
          Alternative embodiments
        </Typography>
        <Typography variant="body2" component="div" sx={{ ml: 1, mb: 1 }}>
          • Variations or optional implementations that achieve similar results.<br />
          • Different configurations, materials, or methods of performing the same function.<br />
          • Even if you don't plan to commercialize all of them, include them — they help broaden protection.
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1.5, mb: 0.5 }}>
          Specific examples or prototypes
        </Typography>
        <Typography variant="body2" component="div" sx={{ ml: 1, mb: 1 }}>
          • Describe one or more practical implementations or test setups.<br />
          • Include experimental data or measured results if available.
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1.5, mb: 0.5 }}>
          Drawings / Diagrams
        </Typography>
        <Typography variant="body2" component="div" sx={{ ml: 1 }}>
          • Numbered figures with corresponding descriptions (Figure 1 shows…, Figure 2 illustrates…).<br />
          • Use reference numerals to tie components in the drawing to the text.
        </Typography>
      </Alert>

      <TextField
        fullWidth
        required
        multiline
        rows={15}
        label="Technical Description and Working Principle"
        value={data.technicalDescriptionAndWorkingPrinciple || ''}
        onChange={(e) => handleChange('technicalDescriptionAndWorkingPrinciple', e.target.value)}
        margin="normal"
        error={data.technicalDescriptionAndWorkingPrinciple && data.technicalDescriptionAndWorkingPrinciple.length < 300}
        helperText={
          data.technicalDescriptionAndWorkingPrinciple && data.technicalDescriptionAndWorkingPrinciple.length < 300
            ? `Must be at least 300 characters (currently ${data.technicalDescriptionAndWorkingPrinciple.length})`
            : "Comprehensive technical description including structure, operation, alternatives, and examples (min. 300 characters)"
        }
      />

      {/* Attachments Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Supporting Diagrams/Images (Optional)
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Upload up to 10 images (JPG, JPEG, PNG). Maximum 2MB per file, 10MB total.
        </Alert>

        {/* Upload Error */}
        {uploadError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError('')}>
            {uploadError}
          </Alert>
        )}

        {/* Upload Stats */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {attachments.length} / 10 files • Total: {getTotalSize()} / 10 MB
          </Typography>
        </Box>

        {/* Attachments Grid */}
        {attachments.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {attachments.map((attachment) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={attachment.id}>
                <Card>
                  <CardMedia
                    component="img"
                    height="120"
                    image={attachment.fileUrl}
                    alt={attachment.fileName}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardActions sx={{ justifyContent: 'space-between', px: 1, py: 0.5 }}>
                    <Typography variant="caption" noWrap sx={{ flex: 1, fontSize: '0.7rem' }}>
                      {attachment.fileName.length > 15
                        ? attachment.fileName.substring(0, 12) + '...'
                        : attachment.fileName}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteAttachment(attachment.id)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                  <Typography variant="caption" color="text.secondary" sx={{ px: 1, pb: 0.5, display: 'block' }}>
                    {formatFileSize(attachment.fileSize)}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Upload Button */}
        {attachments.length < 10 && (
          <Button
            variant="outlined"
            component="label"
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Image'}
            <input
              type="file"
              hidden
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleFileUpload}
              disabled={uploading}
            />
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default DetailedDescription;
